import { Action, createReducer, on } from '@ngrx/store';
import {
  ClearOperationSaveError,
  ClearSelectedOperation,
  ClearSelectedWebService,
  ClearUserSelectedIntegrator,
  ClearWebServiceSaveError,
  CreateOperationError,
  CreateOperationuSuccess,
  CreateUserIntegratorError,
  CreateUserIntegratorSuccess,
  CreateWebServiceConfigError,
  CreateWebServiceConfiguSuccess,
  DeleteOperationSuccess,
  DeleteUserIntegratorSuccess,
  DeleteWebServiceConfigSuccess,
  GetEventsLogIndex,
  GetEventsLogIndexSuccess,
  GetOperationsIndexSuccess,
  GetUserIntegratorIndexSuccess,
  GetWebServiceConfigIndex,
  GetWebServiceConfigIndexSuccess,
  ResetDefaults,
  ResetEventsLog,
  SetEventsLogPageIndex,
  SetEventsLogQuery,
  SetOperationsPageIndex,
  SetOperationsPageSize,
  SetWebServicePageIndex,
  ShowOperationSuccess,
  ShowUserIntegratorSuccess,
  ShowWebServiceConfigHeadersSuccess,
  ShowWebServiceConfigSuccess,
  UpdateOperationError,
  UpdateOperationSuccess,
  UpdateUserIntegratorSuccess,
  UpdateWebServiceConfigError,
  UpdateWebServiceConfigSuccess,
  setOperationsSort,
  setUserIntegratorSort,
  setWebServiceConfigurationsSort
} from './api.actions';
import {
  IApiConfigurationsInterface,
  initialApiConfigurationsState
} from './api.state';

/**
 * Pagina (base 1) que sigue existiendo para la lista y el tamaño dados: la actual si existe, si no la
 * ultima, y 1 si la lista esta vacia. Asi la tabla de operaciones nunca muestra una pagina vacia.
 */
function clampOperationsPageIndex(pageIndex: number, total: number, pageSize: number): number {
  return Math.max(1, Math.min(pageIndex, Math.ceil(total / pageSize)));
}

const reducer = createReducer(
  initialApiConfigurationsState,

  on(GetUserIntegratorIndexSuccess, (state, action) => {
    const newUsers = [...state.users, ...action.payload.client_users];
    const newPage =
      action.payload.client_users.length > 0
        ? state.user_curret_page + 1
        : state.user_curret_page;
    return {
      ...state,
      users: newUsers,
      user_curret_page: newPage,
      create_user: false
    };
  }),

  on(ShowUserIntegratorSuccess, (state, action) => ({
    ...state,
    selected_user: action.payload.client_user
  })),

  on(setWebServiceConfigurationsSort, (state, action) => ({
    ...state,
    web_service_sort_params: action.payload.sort,
    webservice_configs: [],
    webservice_curret_page: 1
  })),

  // Operaciones v3 (feature 023): el orden se aplica en un selector sobre la lista completa, asi que
  // se conserva la lista y solo se regresa a la pagina 1.
  on(setOperationsSort, (state, action) => ({
    ...state,
    operation_sort_params: action.payload.sort,
    operationsPageIndex: 1
  })),

  on(setUserIntegratorSort, (state, action) => ({
    ...state,
    user_sort_params: action.payload.sort,
    user_curret_page: 1,
    users: []
  })),

  on(ShowWebServiceConfigSuccess, (state, action) => ({
    ...state,
    selected_webservice_config: action.payload.webservice_config
  })),

  on(ClearSelectedWebService, (state, action) => ({
    ...state,
    selected_webservice_config: null
  })),

  on(ClearUserSelectedIntegrator, (state, action) => ({
    ...state,
    selected_user: null
  })),

  on(ShowOperationSuccess, (state, action) => ({
    ...state,
    selected_operation: action.payload.operation
  })),

  on(ClearSelectedOperation, (state, action) => ({
    ...state,
    selected_operation: null
  })),

  on(CreateUserIntegratorError, (state, action) => ({
    ...state,
    create_user_errors: action.payload,
    create_user: false
  })),

  on(CreateUserIntegratorSuccess, (state, action) => ({
    ...state,
    create_user: action.payload,
    users: [],
    user_curret_page: 1
  })),

  on(UpdateUserIntegratorSuccess, (state, action) => ({
    ...state,
    create_user: action.payload,
    users: [],
    user_curret_page: 1
  })),

  on(DeleteUserIntegratorSuccess, (state, action) => ({
    ...state,
    users: [],
    user_curret_page: 1
  })),

  on(CreateWebServiceConfiguSuccess, (state, action) => ({
    ...state,
    create_webservice: action.payload,
    webservice_configs: [],
    webservice_curret_page: 1,
    webservicePageIndex: 1,
    webserviceSaveError: null
  })),

  on(UpdateWebServiceConfigSuccess, (state, action) => ({
    ...state,
    update_webservice: action.payload,
    webservice_configs: [],
    webservice_curret_page: 1,
    webservicePageIndex: 1,
    webserviceSaveError: null
  })),

  // Operaciones v3 (feature 023): crear regresa a la pagina 1 y conserva el tamaño y el orden (D8c). La lista
  // se conserva hasta que llegue la recarga: si la recarga falla, la tabla se queda con lo que tenia (U2).
  on(CreateOperationuSuccess, (state) => ({
    ...state,
    operationsPageIndex: 1,
    operationSaveError: null
  })),

  on(CreateOperationError, (state, action) => ({
    ...state,
    operationSaveError: action.payload ?? null
  })),

  // Operaciones v3 (feature 023): editar regresa a la pagina 1 y conserva el tamaño y el orden (D8c). La lista
  // se conserva hasta que llegue la recarga: si la recarga falla, la tabla se queda con lo que tenia (U2).
  on(UpdateOperationSuccess, (state) => ({
    ...state,
    operationsPageIndex: 1,
    operationSaveError: null
  })),

  on(UpdateOperationError, (state, action) => ({
    ...state,
    operationSaveError: action.payload ?? null
  })),

  on(ResetDefaults, (state, action) => ({
    ...state,
    ...initialApiConfigurationsState,
    webservice_configs:[]
  })),

  on(DeleteWebServiceConfigSuccess, (state, action) => ({
    ...state,
    webservice_configs: [],
    webservice_curret_page: 1,
    webservicePageIndex: 1
  })),

  // Operaciones v3 (feature 023): eliminar regresa a la pagina 1 y conserva el tamaño y el orden (D8c). El
  // borrado de un webhook tambien despacha esta accion, asi que su recarga de Operaciones hace lo mismo. La
  // lista se conserva hasta que llegue la recarga: si la recarga falla, la tabla se queda con lo que tenia (U2).
  on(DeleteOperationSuccess, (state) => ({
    ...state,
    operationsPageIndex: 1
  })),

  on(ShowWebServiceConfigHeadersSuccess, (state, action) => ({
    ...state,
    webservice_configs_headers: action.payload.webservice_config_headers
  })),

  // Webhooks v3 (feature 020): la lista completa se guarda tal cual y se pagina en un selector.
  // Crear, editar y eliminar regresan a la pagina 1, igual que v2, que recargaba siempre con page: 1.
  on(GetWebServiceConfigIndex, (state) => ({
    ...state,
    webserviceConfigsLoaded: false
  })),

  on(GetWebServiceConfigIndexSuccess, (state, action) => ({
    ...state,
    webservice_configs: action.payload,
    webserviceConfigsLoaded: true
  })),

  on(SetWebServicePageIndex, (state, action) => ({
    ...state,
    webservicePageIndex: action.payload.pageIndex
  })),

  on(CreateWebServiceConfigError, UpdateWebServiceConfigError, (state, action) => ({
    ...state,
    webserviceSaveError: action.payload ?? null
  })),

  on(ClearWebServiceSaveError, (state) => ({
    ...state,
    webserviceSaveError: null
  })),

  // Operaciones v3 (feature 023): la lista completa se guarda tal cual y se pagina en un selector. Una
  // recarga o un cambio de tamaño con menos filas nunca deja una pagina vacia.
  on(GetOperationsIndexSuccess, (state, action) => ({
    ...state,
    operations: action.payload,
    operationsPageIndex: clampOperationsPageIndex(
      state.operationsPageIndex,
      action.payload.length,
      state.operationsPageSize
    )
  })),

  on(SetOperationsPageIndex, (state, action) => ({
    ...state,
    operationsPageIndex: action.payload.pageIndex
  })),

  on(SetOperationsPageSize, (state, action) => ({
    ...state,
    operationsPageSize: action.payload.pageSize,
    operationsPageIndex: clampOperationsPageIndex(
      state.operationsPageIndex,
      state.operations.length,
      action.payload.pageSize
    )
  })),

  on(ClearOperationSaveError, (state) => ({
    ...state,
    operationSaveError: null
  })),

  // Eventos de webhook v3 (feature 025): GetEventsLogIndex fija la consulta completa y conserva las filas hasta que
  // responda la carga, cuya respuesta reemplaza las filas y el total. Un error de la carga no cambia el estado.
  on(GetEventsLogIndex, (state, action) => ({
    ...state,
    events_query: action.payload
  })),

  on(GetEventsLogIndexSuccess, (state, action) => ({
    ...state,
    events: action.payload.events,
    events_total: action.payload.total
  })),

  // Eventos de webhook v3 (feature 025): el servidor pagina. Un cambio de filtro u orden regresa a la pagina 1 y
  // conserva las filas hasta la siguiente carga; una busqueda invalida vacia la tabla, que no se consulta. Sin una
  // consulta iniciada, estas acciones no cambian nada.
  on(SetEventsLogQuery, (state, action) => {
    if (!state.events_query) {
      return state;
    }

    const events_query = { ...state.events_query, ...action.payload, pageNumber: 1 };

    return events_query.searchInvalid
      ? { ...state, events_query, events: [], events_total: 0 }
      : { ...state, events_query };
  }),

  on(SetEventsLogPageIndex, (state, action) =>
    state.events_query
      ? { ...state, events_query: { ...state.events_query, pageNumber: action.payload } }
      : state
  ),

  // Al salir del panel solo se reinician los campos de eventos; Webhooks, Operaciones y Usuarios no cambian.
  on(ResetEventsLog, (state) => ({
    ...state,
    events: initialApiConfigurationsState.events,
    events_total: initialApiConfigurationsState.events_total,
    events_query: initialApiConfigurationsState.events_query
  })),
);

export function apiConfigurationsReducer(
  state: IApiConfigurationsInterface,
  action: Action
) {
  return reducer(state, action);
}
